import path from 'path';
import fs from 'fs';
import type { AuthStorage as AuthStorageType, ModelRegistry as ModelRegistryType, SessionManager as SessionManagerType } from '@earendil-works/pi-coding-agent';
import type { SuperatomSDK } from '../index';
import { buildDashboardAgentTools } from './tools';

/**
 * Whole-dashboard generation via Pi, a terminal coding agent — as opposed to
 * DASH_COMP_REQ's single-widget-at-a-time flow. Runs Pi in-process via its
 * SDK (createAgentSession), not as a subprocess: no shell, no argument
 * quoting, no stdin/stdout piping, none of the Windows-specific subprocess
 * issues that came with spawning the `pi` CLI directly.
 *
 * Called from sdk-nodejs/src/dashboardAgent/index.ts (DASHBOARD_AGENT_REQ),
 * which owns the generic streaming/abort machinery (mirrors USER_PROMPT_REQ)
 * and passes `signal`/`onProgress` alongside the normal params — this stays
 * within CollectionHandler's loose (params) => Promise<result> typing, no
 * change needed to that shared type.
 *
 * This mechanism is generic and reusable across any deployment. What's
 * genuinely project-specific — where AGENTS.md lives, which model to use —
 * is supplied via `DashboardAgentCollectionConfig`, with defaults sensible
 * enough that most callers don't need to override them (see below). The
 * data-source tool list and the dashboard's current state both come from
 * things sdk-nodejs already exposes generically: `sdk.getTools()` (whatever
 * this deployment registered via `sdk.setTools()`) and `sdk.callCollection
 * ('dashboards', 'query', ...)` (whatever this deployment already registered
 * under that name/shape) — no per-deployment callback needed for either.
 * Same convention on the way out: after a successful run, the prompt and the
 * full response text are handed to `sdk.callCollection('dashboard-agent-
 * conversations', 'create', ...)` if this deployment has registered one —
 * skipped silently otherwise, since conversation history is optional.
 *
 * Pi verifies every query against the live database itself (via whatever
 * local tool-execution bridge the deployment exposes, e.g. an HTTP bridge
 * on localhost), but does NOT persist the result itself — it writes the
 * finished DSL to an absolute path inside `runtimeDir`, told to it explicitly
 * in the prompt, and stops there. This handler reads that file after the run
 * finishes and returns its content as `dashboard` in the result. The caller
 * (frontend) is the one that actually saves it, via whatever authenticated
 * create/update path any other dashboard edit goes through — Pi has no user
 * session/auth context of its own, so persistence shouldn't happen from
 * inside it.
 *
 * Session persistence: the FIRST call for a dashboardId pays the full cost
 * (explore KB, discover schema, plan, verify, build). Every call after that
 * resumes the same session file (SessionManager.open) so Pi has everything
 * it already learned — it only needs to reason about the new, smaller ask,
 * not rediscover the whole dashboard from scratch. The session's file path
 * (AgentSession.sessionFile) is captured right after creation and persisted
 * in a small local file, keyed by dashboardId, inside `runtimeDir`.
 */

export interface DashboardAgentCollectionConfig {
	/**
	 * Working directory Pi runs from — must contain AGENTS.md. This is a
	 * version-controlled prompt file, so `cwd` is expected to live somewhere
	 * like a `.prompts/` folder alongside the deployment's other prompts.
	 * Default: `<process.cwd()>/.prompts/dashboard-agent` — the same
	 * process.cwd()-based convention PromptLoader already uses for the main
	 * agent's prompts, which needs no explicit override in the common case
	 * (the backend process's own cwd already is its project root).
	 */
	cwd?: string;
	/**
	 * Where drafts/, the session-id map, and dashboard.log get written —
	 * separate from `cwd` deliberately, so this deployment's runtime state
	 * (regenerated per session, safe to gitignore) doesn't sit inside the
	 * same folder as the version-controlled AGENTS.md prompt.
	 * Default: `<process.cwd()>/.pi-dashboard-agent-runtime`.
	 */
	runtimeDir?: string;
	/** Model provider (default: process.env.PI_AGENT_PROVIDER || 'openrouter'). */
	provider?: string;
	/** Model id (default: process.env.PI_AGENT_MODEL || 'anthropic/claude-sonnet-4.5'). */
	model?: string;
	/**
	 * true (default): every request starts a brand-new pi session, with the 2
	 * most recent prior responses (if any) injected into the prompt as
	 * context — bounded cost per request, but pi re-explores schema/KB facts
	 * it already verified in an earlier turn on this same dashboard.
	 * false: resumes the same session file across requests on a given
	 * dashboard — pi keeps everything it already learned, but context (and
	 * cost) grows unbounded across turns (one observed turn: 2M+ cache-read
	 * tokens after a handful of edits on the same dashboard).
	 * Default: process.env.PI_AGENT_FRESH_SESSION !== 'false'.
	 */
	freshSession?: boolean;
}

// @earendil-works/pi-coding-agent is pure ESM ("type": "module", no "require"
// export condition) — most deployments compile to CommonJS, so a normal
// `import ... from` (or even a plain `await import(...)`, which TypeScript
// still downlevels to a require() call under module:CommonJS) fails with
// "No exports main defined". Routing the specifier through `new Function`
// hides it from TypeScript's own rewriting, so this actually calls Node's
// real, native dynamic import — the only thing that can bridge CJS -> ESM.
const dynamicImport = new Function('specifier', 'return import(specifier)') as (
	specifier: string
) => Promise<typeof import('@earendil-works/pi-coding-agent')>;

let piModulePromise: ReturnType<typeof dynamicImport> | null = null;
function loadPi() {
	if (!piModulePromise) piModulePromise = dynamicImport('@earendil-works/pi-coding-agent');
	return piModulePromise;
}

// Auth credentials and the model catalog don't change per-request — created
// once (lazily, since it needs the dynamic import above) and reused across
// every call into this collection.
let authAndRegistry: Promise<{ authStorage: AuthStorageType; modelRegistry: ModelRegistryType }> | null = null;
function getAuthAndRegistry() {
	if (!authAndRegistry) {
		authAndRegistry = loadPi().then(({ AuthStorage, ModelRegistry }) => {
			const authStorage = AuthStorage.create();
			const modelRegistry = ModelRegistry.create(authStorage);

			// Same proxy mechanism the main agent's own llm.ts already uses
			// (_openrouterOptions/_proxyBaseURL) — a per-client OpenRouter key
			// (OPENROUTER_API_KEY starting "sk-prox-") is only valid against this
			// deployment's own proxy, not openrouter.ai directly, so requests must
			// be routed through it. The routing itself isn't gated on the key's
			// prefix (same as the main agent) — only on whether the proxy URL is
			// configured at all; the prefix check below is purely a startup
			// warning, mirroring sa.ts's own fail-fast check for the main agent.
			const proxyRoot = process.env.SUPERATOM_LLM_PROXY_URL;
			if (proxyRoot) {
				modelRegistry.registerProvider('openrouter', {
					baseUrl: `${proxyRoot.replace(/\/+$/, '')}/openrouter`,
					apiKey: process.env.OPENROUTER_API_KEY,
				});
			} else if (process.env.OPENROUTER_API_KEY?.startsWith('sk-prox-')) {
				console.warn(
					'[DashboardAgent] OPENROUTER_API_KEY is a proxy key (sk-prox-…) but SUPERATOM_LLM_PROXY_URL is not set — ' +
					'requests would be sent directly to openrouter.ai, which will 401 ("Missing Authentication header"). ' +
					'Set SUPERATOM_LLM_PROXY_URL (e.g. https://ai.superatom.ai) in backend/.env.'
				);
			}

			return { authStorage, modelRegistry };
		});
	}
	return authAndRegistry;
}

/**
 * Pre-resolve which tables are actually relevant to this request via
 * embedding search, for one data source tool — mirrors the main agent's own
 * preResolveSchema (userResponse/agents/main-agent.ts), reusing the exact
 * same 'schema-embeddings' collection/index the backend already populates
 * for every connected source (no new indexing needed). Only worth it for
 * large/very_large-tier sources — smaller ones already fit comfortably in a
 * couple of INFORMATION_SCHEMA queries via the `query` tool. Returns null
 * (not an error) whenever pre-resolution doesn't apply or isn't confident —
 * pi still has `query`'s own INFORMATION_SCHEMA discovery and the
 * keyword-based `schema_search` tool as fallbacks either way.
 */
async function preResolveSchema(sdk: SuperatomSDK, tool: { id: string; schemaTier?: string }, intent: string, projectId?: string): Promise<string | null> {
	const tier = tool.schemaTier;
	if (tier !== 'large' && tier !== 'very_large') return null;

	try {
		const sourceId = tool.id.endsWith('_query') ? tool.id.slice(0, -'_query'.length) : tool.id;
		const res = await sdk.callCollection<{ success?: boolean; results?: Array<{ description: string; similarity: number }> }>(
			'schema-embeddings',
			'search',
			{ query: intent, sourceId, projectId, topK: 30 }
		);
		const results = res?.results ?? [];
		if (!results.length || results[0].similarity < 0.4) return null;

		const highConfidence = results.filter(r => r.similarity >= 0.35);
		if (highConfidence.length < 3) return null;

		const lines = highConfidence.slice(0, 30).map(r => `• ${r.description} [similarity: ${r.similarity.toFixed(2)}]`);
		const header = `[Pre-resolved via embedding search — ${highConfidence.length} most relevant tables in ${tool.id} for this request.]`;
		return `${header}\n${lines.join('\n')}`;
	} catch (err: any) {
		// Missing/unregistered 'schema-embeddings' collection is expected on a
		// deployment that hasn't wired it up — silent skip, same convention as
		// 'dashboard-agent-conversations' elsewhere in this file.
		console.warn(`[DashboardAgent] Schema pre-resolution skipped for ${tool.id}: ${err?.message ?? err}`);
		return null;
	}
}

// Sent through the same onProgress(chunk: string) channel as real text — a
// control signal, not literal output. When pi's own auto-retry restarts a
// turn after a transient error, the previously-streamed (now-abandoned) text
// is still sitting in the frontend's accumulated progress string; without
// this, the retried attempt's near-identical wording gets appended right
// after it, reading as garbled/duplicated text. The frontend clears its
// buffer on seeing this exact marker instead of appending it.
const PROGRESS_RESET_MARKER = ' PI_PROGRESS_RESET ';

/**
 * Turn one of Pi's agent-session events into a short, human-readable
 * progress line. Returns null for events not worth surfacing (reduces
 * noise — e.g. tool_execution_update's partial results, queue bookkeeping).
 */
function describeEvent(event: any): string | null {
	switch (event?.type) {
		case 'tool_execution_start':
			return describeToolStart(event.toolName, event.args) ?? `Running: ${event.toolName}...`;
		case 'tool_execution_end':
			return event.isError ? `Failed: ${event.toolName}` : describeToolEnd(event.toolName, event.result);
		case 'compaction_start':
			return 'Compacting context...';
		case 'auto_retry_start':
			return 'Retrying after an error...';
		default:
			return null;
	}
}

// Kept in sync with script-bootstrap.ts's own PREVIEW_MAX_ROWS/PREVIEW_MAX_CHARS
// (the "scripts flow" child process) — same bounded, char-truncated row
// preview convention, so a query returning many/wide rows doesn't flood the
// live stream with a wall of data pi already has full access to itself.
const PREVIEW_MAX_ROWS = 10;
const PREVIEW_MAX_CHARS = 200;

function previewRows(data: any[]): any[] {
	return data.slice(0, PREVIEW_MAX_ROWS).map(row => {
		if (!row || typeof row !== 'object') return row;
		const out: Record<string, any> = {};
		for (const [k, v] of Object.entries(row)) {
			out[k] = (typeof v === 'string' && v.length > PREVIEW_MAX_CHARS)
				? v.slice(0, PREVIEW_MAX_CHARS) + '…'
				: v;
		}
		return out;
	});
}

// Only the `query` tool's successful completion is worth surfacing here —
// its own result carries a row count/data worth previewing. Every other
// native tool's result is either already implied by its start message
// (kb_search, schema_search, components) or not meaningfully summarizable
// (run_tool's result shape is tool-specific).
function describeToolEnd(toolName: string | undefined, result: any): string | null {
	if (toolName !== 'query') return null;
	try {
		const text = result?.content?.[0]?.text;
		if (!text) return null;
		const parsed = JSON.parse(text);
		const data: any[] = Array.isArray(parsed?.data) ? parsed.data : [];
		const count: number = parsed?.count ?? data.length;
		if (count === 0) return `Query executed — got 0 rows.`;
		const shown = Math.min(count, PREVIEW_MAX_ROWS);
		const header = count > PREVIEW_MAX_ROWS
			? `Query executed — got ${count} rows, showing top ${shown}:`
			: `Query executed — got ${count} row${count === 1 ? '' : 's'}:`;
		// <DataTable> tag, not a ```json code block — same convention
		// script-bootstrap.ts's own query preview already uses, so the frontend
		// renders this as an actual table (see MarkdownRenderer.tsx) rather than
		// a wall of raw JSON text.
		return `${header}\n<DataTable>${JSON.stringify(previewRows(data))}</DataTable>`;
	} catch {
		return null;
	}
}

// Per-tool descriptions for the native tools in tools.ts — args here is the
// real, already-parsed params object (no more scraping SQL out of a bash
// command string via regex, now that queries go through a native `query`
// tool instead of `curl` inside bash). Falls back to null (the generic
// "Running: <toolName>..." message) for pi's own built-in tools (bash, read,
// write, edit) and anything unrecognized.
function describeToolStart(toolName: string | undefined, args: any): string | null {
	switch (toolName) {
		case 'query':
			return args?.sql ? `Running query:\n\`\`\`sql\n${args.sql}\n\`\`\`` : null;
		case 'run_tool':
			return args?.toolId ? `Running tool: ${args.toolId}...` : null;
		case 'schema_search':
			return args?.keywords?.length ? `Searching schema for: ${args.keywords.join(', ')}` : null;
		case 'kb_search':
			return args?.query ? `Searching knowledge base for: "${args.query}"` : null;
		case 'components':
			return 'Fetching component catalog...';
		default:
			return null;
	}
}

/** Assistant text as it streams — the actual "thinking out loud" content. */
function textDelta(event: any): string | null {
	if (event?.type === 'message_update' && event?.assistantMessageEvent?.type === 'text_delta') {
		return event.assistantMessageEvent.delta ?? null;
	}
	return null;
}

// In-flight dashboardIds — a second request for the same dashboard while one
// is already running would race on the same Pi session file. The sdk-nodejs
// message-handling side also tracks its own AbortController per dashboardId;
// this is a separate, collection-local guard against overlapping runs.
const runningDashboards = new Set<string>();

export function registerDashboardAgentCollection(sdk: SuperatomSDK, config: DashboardAgentCollectionConfig = {}): void {
	// process.cwd()-based defaults — the same convention PromptLoader already
	// uses for the main agent's prompts (backend's own cwd already is its
	// project root when running normally, so this needs no override in the
	// common case). Env vars checked here (not just config) so a deployment
	// can relocate either path without any code change in its own backend.
	const piCwd = config.cwd || process.env.PI_AGENT_DIR || path.join(process.cwd(), '.prompts', 'dashboard-agent');
	// Deliberately a separate default from piCwd, not derived from it — piCwd
	// is where AGENTS.md lives (version-controlled prompt content); this is
	// where this deployment's regenerated-per-session state goes, safe to
	// gitignore. Note pi itself only ever knows about piCwd (that's its own
	// working directory) — the draft file path is passed to it explicitly as
	// an absolute path in the prompt below specifically so it lands here, not
	// wherever pi's own relative-path default would put it inside piCwd.
	const runtimeDir = config.runtimeDir || process.env.PI_AGENT_RUNTIME_DIR || path.join(process.cwd(), '.pi-dashboard-agent-runtime');
	// See DashboardAgentCollectionConfig.freshSession's doc comment for the
	// tradeoff — this just resolves which mode is active.
	const freshSession = config.freshSession ?? (process.env.PI_AGENT_FRESH_SESSION !== 'false');
	const SESSIONS_FILE = path.join(runtimeDir, '.pi-dashboard-sessions.json');
	const DRAFTS_DIR = path.join(runtimeDir, 'drafts');
	const DASHBOARD_LOG_FILE = path.join(runtimeDir, 'dashboard.log');

	// One cumulative file, not one per request — the prompt sent to pi plus
	// the actual text it streams back (system instructions are the static
	// AGENTS.md in piCwd, read by pi itself, not something we construct — not
	// logged here). Plain text only, no JSON event dumps — for reading, not
	// parsing.
	function appendLog(text: string): void {
		try {
			fs.mkdirSync(runtimeDir, { recursive: true });
			fs.appendFileSync(DASHBOARD_LOG_FILE, text);
		} catch (err: any) {
			console.warn(`[DashboardAgent] Failed to write dashboard.log: ${err?.message ?? err}`);
		}
	}

	// Overwrites rather than appends — this file is for debugging the most
	// recent run, not a permanent history (that's what the
	// 'dashboard-agent-conversations' collection is for). Always starting
	// fresh means never having to dig through old requests to find the
	// current one.
	function logRequest(dashboardId: string, fullPrompt: string): void {
		const header = `=== [${new Date().toISOString()}] dashboardId=${dashboardId} ===\n--- Prompt ---\n${fullPrompt}\n--- Response ---\n`;
		try {
			fs.mkdirSync(runtimeDir, { recursive: true });
			fs.writeFileSync(DASHBOARD_LOG_FILE, header);
		} catch (err: any) {
			console.warn(`[DashboardAgent] Failed to write dashboard.log: ${err?.message ?? err}`);
		}
	}

	function readDraft(dashboardId: string): any | null {
		try {
			const raw = fs.readFileSync(path.join(DRAFTS_DIR, `${dashboardId}.json`), 'utf-8');
			return JSON.parse(raw);
		} catch (err: any) {
			console.warn(`[DashboardAgent] Failed to read draft for ${dashboardId}: ${err?.message ?? err}`);
			return null;
		}
	}

	function loadSessionMap(): Record<string, string> {
		try {
			return JSON.parse(fs.readFileSync(SESSIONS_FILE, 'utf-8'));
		} catch {
			return {};
		}
	}

	// Value is the session's file path (AgentSession.sessionFile), not an
	// opaque id — that's what SessionManager.open() takes to resume a
	// specific session.
	function saveSessionFile(dashboardId: string, sessionFile: string): void {
		const map = loadSessionMap();
		map[dashboardId] = sessionFile;
		try {
			fs.mkdirSync(piCwd, { recursive: true });
			fs.writeFileSync(SESSIONS_FILE, JSON.stringify(map, null, 2));
		} catch (err: any) {
			console.warn(`[DashboardAgent] Failed to persist session file for ${dashboardId}: ${err?.message ?? err}`);
		}
	}

	sdk.addCollection('dashboard-agent', 'run', async (params: {
		prompt: string;
		dashboardId: string;
		projectId?: string;
		userId?: string;
		selectedWidgetId?: string;
		signal?: AbortSignal;
		onProgress?: (chunk: string) => void;
	}) => {
		const { prompt, dashboardId, projectId, userId, selectedWidgetId, signal, onProgress } = params;
		console.log(`[DashboardAgent] Collection handler invoked. dashboardId=${dashboardId}, projectId=${projectId}, userId=${userId}, promptLength=${prompt?.length}`);

		if (!prompt || !dashboardId) {
			console.log('[DashboardAgent] Rejecting: prompt and dashboardId are required');
			return { success: false, error: 'prompt and dashboardId are required' };
		}

		if (runningDashboards.has(dashboardId)) {
			console.log(`[DashboardAgent] Rejecting: dashboardId=${dashboardId} is already marked as running.`);
			return { success: false, error: 'This dashboard already has an agent run in progress. Please wait for it to finish.' };
		}
		runningDashboards.add(dashboardId);

		try {
			const { createAgentSession, SessionManager } = await loadPi();
			const { authStorage, modelRegistry } = await getAuthAndRegistry();

			// Pinned explicitly rather than left to pi's own default selection —
			// bare pi picks whatever model + max_tokens budget its own
			// auto-selection lands on (seen requesting up to 64k output tokens
			// for a plain "hi"), which both costs more than this task needs and
			// can 402 outright on a low account balance.
			//
			// If a provider/model is explicitly configured (config or env var),
			// that's used as-is with no fallback — an explicit operator choice
			// shouldn't be silently overridden. Otherwise, same "use whichever
			// key is actually configured" idea as the main agent's own provider
			// list (see getLLMProviders()'s DEFAULT_PROVIDERS: anthropic, gemini,
			// openai, groq) — OpenRouter is tried first since that's this
			// deployment's primary path, then the same set the main agent falls
			// back through. Pi's model registry names the gemini/groq providers
			// 'google'/'groq' respectively, but reads credentials from the exact
			// same GEMINI_API_KEY/GROQ_API_KEY env vars sa.ts already sets.
			// modelRegistry.find() only checks the model catalog, not whether
			// credentials exist for it — hasConfiguredAuth() is what actually
			// reflects a configured key, so both are checked per candidate.
			let model: ReturnType<typeof modelRegistry.find>;
			const explicitProvider = config.provider || process.env.PI_AGENT_PROVIDER;
			const explicitModel = config.model || process.env.PI_AGENT_MODEL;
			if (explicitProvider || explicitModel) {
				const providerName = explicitProvider || 'openrouter';
				const modelId = explicitModel || 'anthropic/claude-sonnet-4.5';
				model = modelRegistry.find(providerName, modelId);
				if (!model) {
					return { success: false, error: `Model not found: ${providerName}/${modelId}. Check the configured provider/model and that its API key is configured.` };
				}
			} else {
				const FALLBACK_MODELS = [
					{ provider: 'openrouter', modelId: 'anthropic/claude-sonnet-4.5' },
					{ provider: 'anthropic', modelId: 'claude-sonnet-4-5' },
					{ provider: 'google', modelId: 'gemini-2.5-pro' },
					{ provider: 'openai', modelId: 'gpt-5' },
					{ provider: 'groq', modelId: 'llama-3.3-70b-versatile' },
				];
				for (const candidate of FALLBACK_MODELS) {
					const found = modelRegistry.find(candidate.provider, candidate.modelId);
					if (found && modelRegistry.hasConfiguredAuth(found)) {
						model = found;
						break;
					}
				}
				if (!model) {
					const tried = FALLBACK_MODELS.map(c => `${c.provider}/${c.modelId}`).join(', ');
					return { success: false, error: `No configured model found (tried ${tried}). Configure at least one of OPENROUTER_API_KEY, ANTHROPIC_API_KEY, or OPENAI_API_KEY.` };
				}
			}

			// Told directly, not left for pi to discover/remember itself — it can
			// find the right toolId during its own exploration, but a mid-session
			// hiccup (e.g. an auto-retried turn) can lose track of it, producing a
			// placeholder in the final DSL instead of the real id. Giving it
			// upfront removes that failure mode entirely. Sourced from sdk.getTools()
			// (whatever this deployment registered via sdk.setTools()) — no callback
			// needed, this is already generic. No explicit dialect/type label here
			// (that would need a per-source-type concept sdk-nodejs doesn't have);
			// pi discovers the dialect itself the same way it already verifies
			// every query — its first attempt reveals it via error messages/syntax
			// if the tool name/description doesn't already make it obvious.
			const toolsList = sdk.getTools()
				.map(t => `- ${t.id}: ${t.name}`)
				.join('\n');

			// Pre-resolve which tables are actually relevant to this request via
			// embedding search — mirrors the main agent's own preResolveSchema
			// (userResponse/agents/main-agent.ts), reusing the exact same
			// 'schema-embeddings' collection/index it already populates for every
			// connected source. Without this, pi has to discover large schemas
			// blind — guessing table names into INFORMATION_SCHEMA queries one at
			// a time, which wastes tool calls and sometimes just misses a table
			// entirely (e.g. the earlier session's missed vw_trp_mst_paymenttype_plain).
			// Only worth it for large/very_large-tier sources — smaller ones
			// already fit comfortably in a couple of INFORMATION_SCHEMA queries.
			// Optional at the collection level (skipped silently if this
			// deployment hasn't registered 'schema-embeddings', same convention
			// as 'dashboard-agent-conversations').
			const preResolvedSchemas = (
				await Promise.all(sdk.getTools().map(tool => preResolveSchema(sdk, tool, prompt, projectId)))
			).filter((text): text is string => !!text).join('\n\n');

			// Same reasoning as the toolId fix: leaving component names/render
			// types to be discovered via a tool call (optional, skippable) turned
			// out unreliable in practice — pi invented plausible-looking but
			// nonexistent names instead of the real ones. render.type is
			// precomputed here (not left for pi to derive from the name), so
			// there's no uppercasing/naming step left to get wrong for this part.
			const componentsList = sdk.getComponents()
				.map(c => `- ${c.name} (${c.type}) → render.type: COMP_${c.name.toUpperCase()}`)
				.join('\n');

			// Global KB nodes are the same regardless of what this request is
			// asking — fetched once here instead of on every kb_search call.
			// kb_search itself (tools.ts) no longer re-fetches these; it only
			// returns query-matched (semantic) results going forward. Optional —
			// skipped silently if this deployment hasn't registered
			// 'knowledge-base', same convention as 'dashboard-agent-conversations'.
			let globalKbNodesText = '';
			try {
				const globalResult = await sdk.callCollection<{ nodes?: any[] }>('knowledge-base', 'getGlobal', { limit: 100 });
				if (globalResult?.nodes?.length) {
					globalKbNodesText = JSON.stringify(globalResult.nodes);
				}
			} catch (err: any) {
				console.warn(`[DashboardAgent] Failed to fetch global KB nodes: ${err?.message ?? err}`);
			}

			// The dashboard's actual current state, fetched fresh on every single
			// request and handed over directly — not left as something pi has to
			// remember to check itself. Two things this fixes at once: (1) cost —
			// this would otherwise be a whole extra tool-call round-trip (a full
			// turn re-sending all accumulated context) purely to fetch something
			// the caller already has; (2) correctness — relying on pi to always
			// remember to check current state, especially in a resumed session
			// where it might trust its own stale memory instead (e.g. widgets
			// deleted through the UI since its last turn would still be in its
			// memory, and it could wrongly resurrect them). Making this
			// non-optional removes that whole failure mode, not just the cost.
			// Reuses whatever 'dashboards'.'query' collection this deployment has
			// already registered (the same one the rest of the app uses) instead
			// of a callback purely to re-expose data that's already there.
			let currentDashboardText = 'No dashboard found yet — this is a brand-new dashboard, build it from scratch.';
			try {
				const existing = await sdk.callCollection<{ data?: Array<{ dashboard?: any }> }>('dashboards', 'query', { filters: { dashboardId }, limit: 1 });
				if (existing?.data?.length) {
					currentDashboardText = JSON.stringify(existing.data[0].dashboard);
				}
			} catch (err: any) {
				console.warn(`[DashboardAgent] Failed to fetch current dashboard state for ${dashboardId}: ${err?.message ?? err}`);
			}

			// Only fetched in freshSession mode — resumed sessions already have
			// this in their own history, so it'd be wasted work otherwise.
			// Deliberately capped at the 2 most recent responses, not the full
			// history — responseText includes the query trail (see
			// describeToolStart below), so accumulating every past turn's
			// response would recreate the same unbounded-growth problem
			// freshSession mode exists to avoid, just in a different shape.
			let previousResponseText = '';
			if (freshSession) {
				try {
					const priorConvos = await sdk.callCollection<{ data?: Array<{ responseText?: string }> }>(
						'dashboard-agent-conversations',
						'query',
						{ filters: { dashboardId }, limit: 2, sort: 'DESC' }
					);
					if (priorConvos?.data?.length) {
						// Fetched newest-first — reverse so the prompt reads oldest-to-
						// newest, same chronological order a resumed session would see.
						const turns = [...priorConvos.data].reverse();
						previousResponseText = turns
							.map((turn, i) => `--- Turn ${i + 1} of ${turns.length} ---\n${turn.responseText ?? ''}`)
							.join('\n\n');
					}
				} catch (err: any) {
					console.warn(`[DashboardAgent] Failed to fetch previous responses for ${dashboardId}: ${err?.message ?? err}`);
				}
			}

			// No shell involved (in-process via the SDK), so no cmd.exe
			// argument-quoting concerns — multi-line is fine and more readable
			// for pi to parse.
			const draftPath = path.join(DRAFTS_DIR, `${dashboardId}.json`);
			const fullPrompt = [
				`Dashboard ID: ${dashboardId}.`,
				projectId ? `Project ID: ${projectId}.` : null,
				userId ? `User ID: ${userId}.` : null,
				toolsList ? `Available data source tools (use these exact toolId values in externalTool.toolId — never a placeholder):\n${toolsList}` : null,
				componentsList ? `Available components (use these exact name/type/render.type values — never invent a component name or render.type; call /components for each one's full prop schema before using it):\n${componentsList}` : null,
				globalKbNodesText ? `Global knowledge base nodes (always relevant, given to you once here — kb_search does NOT return these again, only query-matched ones, so don't expect to see them a second time):\n${globalKbNodesText}` : null,
				preResolvedSchemas ? `${preResolvedSchemas}\nThis is a starting point, not guaranteed complete — verify with \`query\`'s INFORMATION_SCHEMA discovery or \`schema_search\` if what's shown here doesn't cover everything the request needs.` : null,
				`Dashboard's actual current state right now (this is authoritative — carry forward every widget you're not asked to change, exactly as shown; if a widget you remember from an earlier turn isn't here anymore, it's gone, don't recreate it):\n${currentDashboardText}`,
				previousResponseText ? `Your response(s) from the most recent prior run(s) on this dashboard (for context on prior reasoning/decisions — the dashboard state above is still the authoritative source of truth for what's actually there now):\n${previousResponseText}` : null,
				selectedWidgetId ? `Scope: the user has widget "${selectedWidgetId}" selected in the UI — "this"/"it" in the request below refers to it specifically, not any other widget. Its current DSL is already in the dashboard state above; look it up there by this id. See AGENTS.md's "Partial vs full output" section for when to default to a partial (single-widget) response instead of the full DSL.` : null,
				`Write your finished DSL to this exact absolute path (create the directory if it doesn't exist): ${draftPath}`,
				`User request: ${prompt}`,
			].filter(Boolean).join('\n');

			logRequest(dashboardId, fullPrompt);

			// See DashboardAgentCollectionConfig.freshSession's doc comment for
			// the tradeoff between these two modes.
			let sessionManager: SessionManagerType;
			if (!freshSession) {
				const existingSessionFile = loadSessionMap()[dashboardId];
				if (existingSessionFile && fs.existsSync(existingSessionFile)) {
					sessionManager = SessionManager.open(existingSessionFile, runtimeDir);
					console.log(`[DashboardAgent] Resuming session file ${existingSessionFile} for dashboard ${dashboardId}`);
				} else {
					sessionManager = SessionManager.create(piCwd, runtimeDir);
					console.log(`[DashboardAgent] Starting a new session for dashboard ${dashboardId}`);
				}
			} else {
				sessionManager = SessionManager.create(piCwd, runtimeDir);
				console.log(`[DashboardAgent] freshSession mode — starting a new session for dashboard ${dashboardId}`);
			}

			// Native tools (query, kb_search, components, get_dashboard) — replaces
			// the HTTP script-bridge pi previously reached by shelling out to curl
			// inside a bash call. See tools.ts for why: clean, structured
			// tool-call events instead of scraping SQL out of a shell command
			// string via regex.
			const customTools = buildDashboardAgentTools(sdk);

			let session;
			try {
				const created = await createAgentSession({
					cwd: piCwd,
					sessionManager,
					authStorage,
					modelRegistry,
					model,
					customTools,
				});
				session = created.session;
			} catch (err: any) {
				// The stored session file may be stale/corrupt (e.g. from an
				// incompatible earlier version) — fall back to a fresh session
				// rather than permanently breaking this dashboard.
				console.warn(`[DashboardAgent] Failed to open existing session, starting fresh: ${err?.message ?? err}`);
				const created = await createAgentSession({
					cwd: piCwd,
					sessionManager: SessionManager.create(piCwd, runtimeDir),
					authStorage,
					modelRegistry,
					model,
					customTools,
				});
				session = created.session;
			}

			// Save immediately — even a failed/aborted run keeps whatever it
			// learned, same principle as before (persist win or lose). Skipped
			// entirely in freshSession mode — there's nothing to resume from, so
			// no point persisting the session file.
			if (!freshSession && session.sessionFile) {
				saveSessionFile(dashboardId, session.sessionFile);
			}

			// Accumulated across the whole run (reset on auto-retry, same as the
			// frontend's own progress buffer via PROGRESS_RESET_MARKER) — this is
			// what gets persisted as the conversation's response text once the
			// run finishes successfully.
			let responseText = '';

			const unsubscribe = session.subscribe((event: any) => {
				if (event?.type === 'auto_retry_start') {
					onProgress?.(PROGRESS_RESET_MARKER);
					appendLog(`\n\n--- [retrying: ${event.errorMessage ?? 'transient error'}] ---\n\n`);
					responseText = '';
				}

				const delta = textDelta(event);
				if (delta) {
					onProgress?.(delta);
					appendLog(delta);
					responseText += delta;
					return;
				}

				const description = describeEvent(event);
				if (description) {
					// Double newlines, not single — standard markdown (what the
					// frontend renders this through) treats a single \n as just a
					// space within the same paragraph, not a line break. Without
					// this, "Running: bash..." visually runs together with
					// surrounding text in the rendered UI even though it looks
					// fine as plain text in dashboard.log.
					const chunk = `\n\n${description}\n\n`;
					onProgress?.(chunk);
					appendLog(chunk);
					// Also folded into responseText (not just streamed live) — so
					// reopening a saved conversation later shows the same query
					// trail that was visible during the live run, not just the
					// final prose summary.
					responseText += chunk;
				}
			});

			const onAbort = () => {
				console.log(`[DashboardAgent] Aborting session for dashboard ${dashboardId}`);
				// session.abort() alone was observed to not actually stop a run —
				// it likely only prevents the NEXT turn from starting, while an
				// in-flight bash call (pi spends most of its time running one —
				// SQL checks, schema exploration, writing the draft) keeps
				// running to completion and the loop continues past it. Kill
				// the in-flight bash command directly too, synchronously,
				// before session.abort() even resolves.
				if (session.isBashRunning) {
					console.log(`[DashboardAgent] Bash command in flight — killing it directly for dashboard ${dashboardId}`);
					session.abortBash();
				}
				session.abort().catch((err: any) => console.warn(`[DashboardAgent] abort() failed: ${err?.message ?? err}`));
			};
			signal?.addEventListener('abort', onAbort, { once: true });

			try {
				await session.prompt(fullPrompt);
			} catch (err: any) {
				if (!signal?.aborted) {
					console.error(`[DashboardAgent] session.prompt() failed for dashboard ${dashboardId}: ${err?.message ?? err}`);
					return { success: false, error: err?.message ?? String(err) };
				}
			} finally {
				unsubscribe();
				signal?.removeEventListener('abort', onAbort);
				try {
					const stats = session.getSessionStats();
					const costLine = `Model: ${model?.provider}/${model?.id} — Cost: $${stats.cost.toFixed(4)} — tokens: ${stats.tokens.input} in / ${stats.tokens.output} out / ${stats.tokens.cacheRead} cache-read / ${stats.tokens.cacheWrite} cache-write (${stats.tokens.total} total), ${stats.toolCalls} tool calls`;
					console.log(`[DashboardAgent] ${costLine} — dashboard ${dashboardId}`);
					appendLog(`\n\n[cost] ${costLine}\n`);
				} catch (err: any) {
					console.warn(`[DashboardAgent] Failed to read session stats: ${err?.message ?? err}`);
				}
				session.dispose();
			}

			if (signal?.aborted) {
				return { success: false, error: 'aborted' };
			}

			const dashboard = readDraft(dashboardId);
			if (!dashboard) {
				return { success: false, error: 'pi finished but did not write a draft file — nothing to persist.' };
			}

			// Passed through to the frontend as-is below — this is purely a shape
			// sanity check so a malformed draft fails loudly here instead of
			// silently reaching the frontend's merge step as something it can't
			// handle. The actual merge (splicing a partial response into the saved
			// dashboard) happens frontend-side, not here — this collection's job
			// ends at handing back whatever pi wrote.
			const isValidFull = dashboard.mode === 'full' && dashboard.dsl;
			const isValidPartial = dashboard.mode === 'partial' && dashboard.widgets &&
				(Array.isArray(dashboard.widgets.upsert) || Array.isArray(dashboard.widgets.remove));
			if (!isValidFull && !isValidPartial) {
				return { success: false, error: `pi wrote a draft with an unrecognized shape (mode="${dashboard.mode}") — expected {mode:"full",dsl} or {mode:"partial",widgets:{upsert,remove}}.` };
			}

			// Only successful runs are recorded — a failed/aborted run has
			// nothing useful to show in a conversation history. Optional: skips
			// silently if this deployment hasn't registered a
			// 'dashboard-agent-conversations' collection (see
			// DashboardAgentCollectionConfig's doc comment for the convention).
			if (responseText.trim()) {
				try {
					await sdk.callCollection('dashboard-agent-conversations', 'create', {
						dashboardId,
						projectId,
						userId,
						userPrompt: prompt,
						responseText,
					});
				} catch (err: any) {
					console.warn(`[DashboardAgent] Failed to save conversation history for ${dashboardId}: ${err?.message ?? err}`);
				}
			}

			return { success: true, dashboardId, dashboard };
		} finally {
			runningDashboards.delete(dashboardId);
		}
	});

	console.log('✓ dashboard-agent collection registered (Pi SDK, in-process)');
}
